import { QueryClient, QueryCache } from '@tanstack/vue-query';
import { handleError } from '@/utils/errorHandling';
import { datadogLogs } from '@datadog/browser-logs';

// Extend QueryMeta to include our custom options
declare module '@tanstack/vue-query' {
	interface Register {
		queryMeta: {
			errorToast?: {
				disabled?: boolean;
				title?: string;
				description?: string;
			};
		};
	}
}

/**
 * Global QueryClient
 *
 */
export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error, query) => {
			// ALWAYS handle error (logs to Datadog in production)
			// Use meta.errorToast to customize toast behavior:
			// - { disabled: true } - no toast, but still logs to Datadog
			// - { title, description } - custom toast, logs to Datadog
			// - undefined - generic toast, logs to Datadog
			const errorToastConfig = query.options.meta?.errorToast;

			if (errorToastConfig?.disabled) {
				// Don't show toast, but still log to Datadog for monitoring
				if (import.meta.env.DEV) {
					console.error('[Query Error]', error);
				} else if (error instanceof Error) {
					datadogLogs.logger.error('[Query Error]', {
						error: {
							stack: error.stack,
							message: error.message,
						},
						queryKey: query.queryKey,
					});
				}
				return;
			}

			// Show toast with custom or default messaging (also logs to Datadog)
			handleError(error, {
				title: errorToastConfig?.title,
				description: errorToastConfig?.description,
			});
		},
	}),
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			staleTime: 0,
			gcTime: 5 * 60 * 1000,
			retry: (failureCount, error) => {
				// Don't retry on 4xx errors (client errors)
				if (error && typeof error === 'object' && 'response' in error) {
					const axiosError = error as any;
					if (axiosError.response?.status >= 400 && axiosError.response?.status < 500) {
						return false;
					}
				}
				// Retry up to 2 times for other errors (3 total attempts)
				// Note: Each retry does NOT trigger a new toast since we removed Axios interceptor handling
				return failureCount < 2;
			},
		},
		mutations: {
			retry: false, // Don't retry mutations by default
			// Default error handler - acts as FALLBACK (gets overridden by local onError)
			onError: (error) => {
				handleError(error);
			},
		},
	},
});
